import { Article } from "@/globalTypes";

export const getResponsiveImageUrl = (article: Article) => {
    if (!article?.banner) return '';

    // This helper is reachable from server-rendered listings (article tags, the
    // articles index, blog detail), where `window` does not exist. Reading
    // window.innerWidth unguarded here threw a ReferenceError during SSR and
    // returned a 500 for every seeded listing that actually had results — which
    // is what took ~190 /articles/tags/* pages out of Google's index. Callers
    // should still gate on an `isMounted` flag so the server HTML matches the
    // first client render; this guard is the backstop.
    if (typeof window === 'undefined') {
        return article.banner.thumbnail?.url || article.banner.small?.url || '';
    }

    // Get window width to determine the device type
    const width = window.innerWidth;

    // Mobile: width <= 768px
    if (width <= 768) {
        return article.banner.small?.url || article.banner.thumbnail?.url || '';
    }

    // Tablet: width > 768px and <= 1024px
    if (width > 768 && width <= 1024) {
        return (
            article.banner.medium?.url ||
            article.banner.small?.url ||
            article.banner.thumbnail?.url ||
            ''
        );
    }

    // Desktop: width > 1024px
    return (
        article.mainImage?.url || // New main image for desktop
        article.banner.large?.url || 
        article.banner.medium?.url || 
        article.banner.small?.url || 
        article.banner.thumbnail?.url || 
        ''
    );
};

export const getLocaleFromAcceptLanguage = (acceptLanguage: string ): string => {
    const localeMap: { [key: string]: string } = {
        'en': 'en',
        'hi': 'hi-IN',
        'ta': 'en',
        'te': 'en',
        'mr': 'en',
        'gu': 'en',
        'kn': 'en',
        'ml': 'en',
    };

    return localeMap[acceptLanguage] || 'en';
};

// The deities API is case-sensitive and keys on an underscored lowercase name
// ("ganesha", "lakshmi_narasimha"), while URLs carry a hyphenated slug. Older
// links also used raw spaces ("/deities/maa baglamukhi"), which arrive as %20.
// Shared by the deity page (server 404 check + canonical) and DeityView's fetch
// so the two can never disagree about which deity a URL means.
export const toDeityApiKey = (slug: string) => {
    let raw = slug;
    try {
        raw = decodeURIComponent(slug);
    } catch {
        // Malformed percent-encoding — fall back to the raw slug rather than throwing.
    }
    return raw.trim().toLowerCase().replaceAll(" ", "_").replaceAll("-", "_");
};

// Canonical URL form of a deity slug: lowercase, hyphenated, no stray spaces.
export const toDeityCanonicalSlug = (slug: string) =>
    toDeityApiKey(slug).replaceAll("_", "-");

// The festival page looks festivals up by their stored slug; names drift from it
// (renames, spelling), so prefer the slug and fall back to the name-derived form.
export const festivalSlug = (festival: { slug?: string; name: string }) =>
  festival.slug ||
  festival.name.trim().replaceAll("-", " ").split(" ").filter((subtext: string) => subtext !== "").join("-").toLowerCase();

export const formatMyStringAsSlug = (text: string) =>
  text
    .replace(/[^a-zA-Z\s]/g, "") // Filter out non-alphabetic and non-space characters
    .trim()
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .toLowerCase();

// Festival timestamps mark a calendar date at IST midnight (18:30 UTC the day
// before). Reading them with the runtime's local zone put festivals on the
// previous day whenever that zone wasn't IST — on a UTC server, or for a visitor
// abroad — so the server HTML and the browser disagreed. Read them in IST.
const IST_OFFSET_MS = 330 * 60 * 1000;
export const istDateParts = (unixSeconds: number) => {
  const d = new Date(unixSeconds * 1000 + IST_OFFSET_MS);
  return { year: d.getUTCFullYear(), month: d.getUTCMonth(), date: d.getUTCDate() };
};

// The year a festival page is about: the IST year of its next occurrence, the
// same date the H1 and Event schema state. Undefined when the API has no date.
export const festivalYearOf = (festival?: { upcoming_dates?: number[] | null } | null) => {
  const next = festival?.upcoming_dates?.[0];
  return next ? istDateParts(next).year : undefined;
};

// Start of today's IST calendar date, as a UTC-midnight Date. The server renders
// in UTC, so a plain setHours(0, 0, 0, 0) still says "yesterday" from 00:00 to
// 05:30 IST. The panchanga API picks its day by IST date, which this timestamp
// (05:30 IST) lands on, and getDate()/getMonth() read back the IST date on a UTC
// server.
export const startOfTodayIST = (): Date => {
    const nowInIST = new Date(Date.now() + IST_OFFSET_MS);
    return new Date(Date.UTC(nowInIST.getUTCFullYear(), nowInIST.getUTCMonth(), nowInIST.getUTCDate()));
};

// Today's date in IST as DD/MM/YYYY — what the prediction API is keyed on. The
// server's own zone (UTC in production) would still be on yesterday until 05:30 IST.
export const istTodayDDMMYYYY = () => {
  const { year, month, date } = istDateParts(Date.now() / 1000);
  return `${String(date).padStart(2, '0')}/${String(month + 1).padStart(2, '0')}/${year}`;
};
